/**
 * Teks mode suara panel asisten (id/en). `{name}` = nama asisten,
 * `{waktu}` = sisa waktu (m:ss), `{durasi}` = durasi tunggu kuota.
 */

export interface AssistantVoiceText {
	start: string;
	/** Sub-teks kecil di bawah judul tombol mulai. */
	startHint: string;
	stop: string;
	status: {
		connecting: string;
		ready: string;
		listening: string;
		thinking: string;
		answering: string;
	};
	micActive: string;
	muteAnswer: string;
	unmuteAnswer: string;
	muteMic: string;
	unmuteMic: string;
	remaining: string;
	extendInvite: string;
	/** Label baris kata pengisi GPT-Live ("{name}: Siap.") — beda dari transkrip ucapan user. */
	fillerLabel: string;
	extend: string;
	voiceMarker: string;
	dismiss: string;
	unsupported: string;
	/** Dibacakan GPT-Live saat ucapan tak terbaca / jawaban gagal diambil. */
	commentary: {
		empty: string;
		failed: string;
	};
	consent: {
		title: string;
		body: string;
		accept: string;
		decline: string;
	};
	ended: {
		idle: string;
		maxDuration: string;
		quota: string;
		closed: string;
	};
	errors: {
		micDenied: string;
		quota: string;
		slotEmpty: string;
		/** Layanan suara sedang gangguan; ramah untuk user biasa dan tidak menyebut vendor. */
		unavailable: string;
		connectionLost: string;
		secondTab: string;
		forbidden: string;
		wallOff: string;
		failed: string;
		/** Browser memblokir audio jawaban; teks tetap tampil. */
		audioBlocked: string;
		/** Jawaban diambil lewat fallback (GPT-Live tak mendelegasikan): hanya teks di panel. */
		fallbackAnswer: string;
		/** Penutupan sesi di server gagal (server menutupnya sendiri setelah heartbeat basi). */
		closeFailed: string;
	};
	/** Kontrol Mode NOC di /wall: lencana mikrofon dan tombol tekan-untuk-bicara. */
	noc: {
		micClosed: string;
		micOpen: string;
		/** `{name}` = nama asisten. */
		aiSpeaking: string;
		pttHold: string;
		pttHolding: string;
		pttSpaceHint: string;
		/** `{name}` = nama asisten. */
		autoMuteHint: string;
		typedNotRead: string;
	};
}

const id: AssistantVoiceText = {
	start: "Mulai bicara dengan {name}",
	startHint: "Ngobrol langsung, tanpa mengetik",
	stop: "Matikan mode suara",
	status: {
		connecting: "Menghubungkan…",
		ready: "Siap bicara",
		listening: "Mendengarkan…",
		thinking: "Memproses…",
		answering: "Menjawab…",
	},
	micActive: "Mikrofon aktif",
	muteAnswer: "Bisukan suara jawaban",
	unmuteAnswer: "Bunyikan suara jawaban",
	muteMic: "Matikan mikrofon",
	unmuteMic: "Nyalakan mikrofon",
	remaining: "Sisa {waktu}",
	extendInvite: "Sesi suara hampir habis. Perpanjang 1 menit?",
	fillerLabel: "{name}:",
	extend: "Perpanjang",
	voiceMarker: "Pesan suara",
	dismiss: "Tutup pesan",
	unsupported: "Mode suara hanya tersedia di Chrome atau Edge versi desktop.",
	commentary: {
		empty: "Ucapan tidak terbaca. Mohon ulangi.",
		failed: "Maaf, jawabannya gagal diambil. Silakan tanyakan lagi.",
	},
	consent: {
		title: "Izinkan mode suara",
		body: "Selama mode suara aktif, mikrofon Anda menyala dan suara Anda dikirim ke OpenAI untuk diproses. Suara jawaban {name} dibuat oleh AI. Rekaman suara tidak disimpan.",
		accept: "Setuju dan mulai",
		decline: "Batal",
	},
	ended: {
		idle: "Mode suara dimatikan karena tidak ada percakapan.",
		maxDuration: "Batas waktu sesi suara tercapai.",
		quota: "Jatah menit suara hari ini sudah habis.",
		closed: "Sesi suara berakhir.",
	},
	errors: {
		micDenied:
			"Akses mikrofon ditolak. Izinkan mikrofon di pengaturan browser lalu coba lagi.",
		quota: "Jatah menit suara hari ini sudah habis. Coba lagi dalam {durasi}.",
		slotEmpty: "Mode suara belum disiapkan admin.",
		unavailable:
			"Mode suara sedang tidak tersedia. Chat teks tetap bisa dipakai; silakan ketik pertanyaan Anda.",
		connectionLost: "Koneksi suara terputus. Silakan mulai lagi.",
		secondTab:
			"Mode suara sedang aktif di tab atau perangkat lain. Matikan di sana dulu.",
		forbidden: "Akun Anda tidak diizinkan memakai mode suara.",
		wallOff: "Suara di layar wall sedang dinonaktifkan admin.",
		failed: "Mode suara gagal dimulai. Coba lagi sebentar.",
		audioBlocked:
			"Browser memblokir suara jawaban. Jawaban tetap tampil sebagai teks; klik di halaman lalu coba lagi.",
		fallbackAnswer:
			"Jawaban ini hanya tampil sebagai teks di panel dan tidak dibacakan.",
		closeFailed:
			"Sesi suara gagal ditutup di server. Sesi akan ditutup otomatis dalam beberapa menit.",
	},
	noc: {
		micClosed: "Mikrofon TERTUTUP",
		micOpen: "Mikrofon TERBUKA",
		aiSpeaking: "{name} berbicara",
		pttHold: "Tahan untuk bicara",
		pttHolding: "Lepas bila selesai",
		pttSpaceHint: "Atau tahan tombol Spasi",
		autoMuteHint: "Mikrofon dibisukan otomatis saat {name} berbicara",
		typedNotRead: "Teks yang diketik tidak dibacakan",
	},
};

const en: AssistantVoiceText = {
	start: "Start talking with {name}",
	startHint: "Talk hands-free, no typing",
	stop: "Turn off voice mode",
	status: {
		connecting: "Connecting…",
		ready: "Ready to talk",
		listening: "Listening…",
		thinking: "Thinking…",
		answering: "Answering…",
	},
	micActive: "Microphone on",
	muteAnswer: "Mute answer voice",
	unmuteAnswer: "Unmute answer voice",
	muteMic: "Mute microphone",
	unmuteMic: "Unmute microphone",
	remaining: "{waktu} left",
	extendInvite: "Voice session is almost over. Extend by 1 minute?",
	fillerLabel: "{name}:",
	extend: "Extend",
	voiceMarker: "Voice message",
	dismiss: "Dismiss message",
	unsupported: "Voice mode is only available in desktop Chrome or Edge.",
	commentary: {
		empty: "I couldn't make out what you said. Please repeat.",
		failed: "Sorry, I couldn't get the answer. Please ask again.",
	},
	consent: {
		title: "Allow voice mode",
		body: "While voice mode is on, your microphone is active and your voice is sent to OpenAI for processing. {name}'s answer voice is generated by AI. Audio is not stored.",
		accept: "Agree and start",
		decline: "Cancel",
	},
	ended: {
		idle: "Voice mode turned off because nobody was talking.",
		maxDuration: "The voice session time limit was reached.",
		quota: "Today's voice minutes are used up.",
		closed: "The voice session has ended.",
	},
	errors: {
		micDenied:
			"Microphone access was denied. Allow the microphone in your browser settings and try again.",
		quota: "Today's voice minutes are used up. Try again in {durasi}.",
		slotEmpty: "Voice mode has not been set up by an admin.",
		unavailable:
			"Voice mode is temporarily unavailable. Text chat still works; please type your question.",
		connectionLost: "The voice connection was lost. Please start again.",
		secondTab:
			"Voice mode is already active in another tab or device. Turn it off there first.",
		forbidden: "Your account is not allowed to use voice mode.",
		wallOff: "Voice on the wall display is turned off by the admin.",
		failed: "Voice mode failed to start. Please try again shortly.",
		audioBlocked:
			"The browser blocked the answer audio. The answer is still shown as text; click the page and try again.",
		fallbackAnswer:
			"This answer is shown as text in the panel only and is not read aloud.",
		closeFailed:
			"The voice session could not be closed on the server. It will close automatically in a few minutes.",
	},
	noc: {
		micClosed: "Microphone OFF",
		micOpen: "Microphone ON",
		aiSpeaking: "{name} is speaking",
		pttHold: "Hold to talk",
		pttHolding: "Release when done",
		pttSpaceHint: "Or hold the Space key",
		autoMuteHint: "The microphone is muted automatically while {name} speaks",
		typedNotRead: "Typed text is not read aloud",
	},
};

export const assistantVoiceTexts = { id, en } as const;
